import { jsx as _jsx } from "react/jsx-runtime";
import './GraphvizViewer.scss';
import { toDot } from 'ts-graphviz';
import { graphviz } from 'd3-graphviz';
import { calculatePathwayAsDigraphImpl } from './functions.js';
import { cloneObj } from '@nu-art/ts-common';
import { ComponentSync, EventType_Create, ThunderDispatcher } from '@nu-art/thunderstorm-frontend/index';
import { ModuleFE_PathwayState, ModuleFE_PathwayTransition } from '../../_entity.js';
import { select } from 'd3';
export const dispatch_onCreateTransition = new ThunderDispatcher('__onCreateTransition');
export const dispatch_onTransitionCreationDone = new ThunderDispatcher('__onTransitionCreationDone');
export class GraphvizPathwayViewer extends ComponentSync {
    ref;
    componentDidMount() {
        window.addEventListener('keydown', this.toggleCreateTransition);
        window.addEventListener('keyup', this.removeMouseListener);
    }
    componentWillUnmount() {
        window.removeEventListener('keydown', this.toggleCreateTransition);
        window.removeEventListener('keyup', this.removeMouseListener);
    }
    __onPathwayStateUpdated = (...params) => {
        if (params[0] === EventType_Create)
            this.reDeriveState({ selectedStateIds: [params[1]._id] });
        else
            this.reDeriveState();
    };
    __onPathwayTransitionUpdated = (...params) => {
        this.reDeriveState();
    };
    __onTransitionCreationDone(selectedStateId) {
        this.reDeriveState({ transitionNodes: undefined, selectedStateIds: [selectedStateId] });
    }
    deriveStateFromProps(nextProps, state) {
        state.states = ModuleFE_PathwayState.cache.filter(state => state.pathwayIds.includes(nextProps.pathwayId));
        state.transitions = ModuleFE_PathwayTransition.cache.filter(transition => transition.pathwayIds.includes(nextProps.pathwayId));
        if (nextProps.viewerMode)
            state.selectedStateIds = nextProps.selectedStateIds;
        state.digraph = this.buildGraph(state.states, state.transitions, state.selectedStateIds, state.transitionNodes);
        state.transitionCreationMode ??= false;
        state.transitionNodes ??= {};
        this.renderGraphviz(state.digraph); // if called before mounting, will skip
        return state;
    }
    toggleCreateTransition = (e) => {
        if (e.metaKey)
            this.reDeriveState({ transitionCreationMode: true, selectedStateIds: undefined, transitionNodes: undefined });
    };
    removeMouseListener = (e) => {
        if (e.key === 'Meta') {
            this.reDeriveState({ transitionCreationMode: false, ...(!this.state.transitionNodes?.targetId || !this.state.transitionNodes?.sourceId) && { transitionNodes: undefined } });
        }
    };
    updateTransitionNodes = (stateId) => {
        const transitionNodes = this.state.transitionNodes && cloneObj(this.state.transitionNodes);
        if (transitionNodes?.sourceId && !transitionNodes?.targetId) {
            transitionNodes.targetId = stateId;
        }
        else if (!transitionNodes?.sourceId) {
            transitionNodes.sourceId = stateId;
        }
        this.shouldDispatch(transitionNodes);
        this.reDeriveState({ transitionNodes: transitionNodes });
    };
    shouldDispatch = (transitionNodes) => {
        if (transitionNodes?.sourceId && transitionNodes?.targetId)
            dispatch_onCreateTransition.dispatchUI(transitionNodes);
    };
    buildGraph(states, transitions, selectedStateIds, transitionNodes) {
        return calculatePathwayAsDigraphImpl(states, transitions, selectedStateIds ?? (transitionNodes && Object.values(transitionNodes)), this.props.dpModule);
    }
    renderGraphviz(digraph) {
        if (!this.ref)
            return;
        if (!this.mounted || !digraph)
            return;
        const dotString = toDot(digraph);
        graphviz(this.ref, { useWorker: false, zoomScaleExtent: [0.5, 2] })
            .renderDot(dotString)
            .on('renderEnd', () => {
            const selection = select(this.ref).selectAll('.node');
            selection.on('click', (e) => {
                const nodeId = e.currentTarget.id; // each node is given state._id as it's element id
                this.logInfo('clicked node', nodeId);
                if (!this.state.transitionCreationMode) {
                    this.props.onStateClick?.(nodeId);
                    this.reDeriveState({ selectedStateIds: [nodeId], transitionNodes: undefined });
                }
                else {
                    this.updateTransitionNodes(nodeId);
                }
            });
        });
    }
    getDigraphNodes() {
        return this.state?.digraph?.subgraphs.find(sg => sg.id === 'Container')?.nodes;
    }
    getNodeById(stateId) {
        const digraphNodes = this.getDigraphNodes();
        if (!digraphNodes)
            return;
        const clickedNodeIndex = digraphNodes.findIndex(node => node.id === stateId);
        return clickedNodeIndex > -1 ? digraphNodes[clickedNodeIndex] : undefined;
    }
    viewerMode() {
        return !!this.props.viewerMode;
    }
    render() {
        if (!this.state?.digraph)
            return _jsx("div", { className: 'page-message', children: "No pathway to show" });
        return _jsx("div", { id: 'pathway-viewer', ref: ref => {
                if (this.ref || !ref)
                    return;
                this.ref = ref;
                this.renderGraphviz(this.state?.digraph);
            } });
    }
}
//# sourceMappingURL=GraphvizPathwayViewer.js.map